import apiFetch from 'react-scale/lib/api-fetch';
import {
  ISLAJson,
  IContractJson,
  BillingUnit,
  IBillingRateJson,
} from '@scale/shared/types/contract';

import { useAsync } from 'react-use-v13';
import QMLoadingPage from 'react-scale/corp/components/QM/QMLoadingPage';
import QMErrorPage from 'react-scale/corp/components/QM/QMErrorPage';
import Portal from 'react-scale/corp/components/SmartLink/PreviewPortal';
import React from 'react';
import { TargetProjectStrategy } from '@scale/shared/corp/dynamicCorpTools/deepProjectClone';

const WorkAttributesList: React.FC<{
  workAttributes: Record<string, any>;
  getColorOfWorkAttribute: CallableFunction;
}> = ({ workAttributes, getColorOfWorkAttribute }) => {
  const normalizedList = React.useMemo(() => {
    const items = Object.entries(workAttributes);
    const normalized = items.sort(([keyA], [keyB]) => (keyA < keyB ? -1 : 1));
    return normalized;
  }, [workAttributes]);

  return (
    <div className={'work-attributes'}>
      <style jsx>
        {`
          .work-attributes {
            display: flex;
            flex-wrap: wrap;
          }
          .work-attribute {
            display: flex;
            flex-direction: row;
            margin-right: 5px;
            margin-bottom: 5px;
            border-radius: 5px;
            border: 2px solid transparent;
            font-size: smaller;
          }

          .work-attribute__key {
            color: white;
            font-style: italic;
            align-text: center;
            text-transform: capitalize;
            padding: 3px 5px;
          }

          .work-attribute__value {
            color: black;
            font-weight: bold;
            align-text: center;
            padding: 3px 5px;
            background: white;
          }
        `}
      </style>
      {normalizedList.map(([key, value]) => {
        return (
          <div
            key={key}
            className={'work-attribute'}
            style={{ borderColor: getColorOfWorkAttribute(key) }}
          >
            <div
              className={'work-attribute__key'}
              style={{ background: getColorOfWorkAttribute(key) }}
            >
              {key}
            </div>
            <div className={'work-attribute__value'}>{value}</div>
          </div>
        );
      })}
    </div>
  );
};

const SLAPill: React.FC<{
  sla: ISLAJson;
  contract: IContractJson;
  isActive?: boolean;
  onClick?: CallableFunction;
  getColorOfWorkAttribute: CallableFunction;
}> = ({ sla, contract, onClick, isActive, getColorOfWorkAttribute }) => {
  const colorOfBillingUnit = sla.billingUnit === BillingUnit.Hour ? '#0076FF' : '#000000';
  return (
    <div className={'box ' + (isActive ? 'box--active' : '')} onClick={() => onClick?.()}>
      <style jsx>{`
        .box {
          border: 1px solid #ccc;
          cursor: pointer;
          max-width: 500px;
          display: flex;
        }

        .rows {
          padding: 10px;
          flex-grow: 1;
        }

        .billing-unit {
          writing-mode: vertical-rl;
          text-orientation: mixed;
          text-transform: uppercase;
          align-text: center;
          display: flex;
          justify-content: center;
          align-content: center;
          color: white;
          padding: 5px;
        }

        .box.box--active,
        .box.box--active:hover {
          background: #28f;
        }
        .box:hover {
          background: #eee;
        }

        .row {
          display: flex;
          flex-grow: 1;
        }

        .space {
          flex-grow: 1;
        }
        .sla-name {
          font-weight: bold;
        }

        .task-type {
          text-align: right;
        }

        .contract-name {
          color: limegreen;
          font-weight: bold;
        }

        .sla-id {
          margin-left: 8px;
          color: gray;
        }

        .billing-spec {
          font-size: smaller;
          color: gray;
          padding-top: 3px;
        }

        .box--active .billing-spec {
          color: white;
        }

        .box a {
          margin: 0 0 0 8px;
        }

        .box a:hover {
          text-decoration: underline;
        }

        .box--active a {
          color: white;
        }

        .dummy-tag {
          margin: 5px;
          font-size: smaller;
          padding: 3px;
          border: 1px solid #28c;
          color: #28c;
        }

        .box--active .dummy-tag {
          color: white;
          border: 1px solid white;
        }
      `}</style>
      <div className={'billing-unit'} style={{ background: colorOfBillingUnit }}>
        {sla.billingUnit ?? BillingUnit.Task}
      </div>
      <div className={'rows'}>
        <div className={'row'}>
          <div className={'sla-name'}>
            {sla.isAutoCreatedDummySla && <span className="dummy-tag">$0 AUTOGEN</span>} {sla.name}
          </div>
          <div className={'space'} />
          <div className={'task-type'}>{sla.taskType}</div>
        </div>
        <div className={'row'}>
          <div className={'contract-name'}>
            {sla.contract?.salesOrder}
            <a href={'/corp/lookup/' + contract?._id} rel="noopener noreferrer" target={'_blank'}>
              Contract
            </a>
            <a href={'/corp/lookup/' + sla._id} rel="noopener noreferrer" target={'_blank'}>
              SLA
            </a>
          </div>
          <div className={'space'} />
          <div className={'created-date'}>{new Date(sla.createdAt).toLocaleString()}</div>
        </div>
        <div className={'row'}>
          <div className={'sla-id'}>SLA ID: {sla._id}</div>
        </div>
        <div className={'row'}>
          <div className={'billing-spec'}>{JSON.stringify(sla.billingSpec, null, 2)}</div>
        </div>
        {sla.billingUnit === BillingUnit.Hour && sla.billingRate && (
          <div className={'row'}>
            <WorkAttributesList
              workAttributes={sla.billingRate.workAttributes}
              getColorOfWorkAttribute={getColorOfWorkAttribute}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const colorsByIndex = ['#0076FF', '#29CD9C', '#C1EAD9', '#F8BB06', '#E8412A', '#6a5df9'];

export const SLAPicker: React.FC<{
  taskCustomer: string;
  taskType: string;
  defaultSla?: string;
  onChange?: CallableFunction;
  expandInPlace?: boolean;
  align?: 'left' | 'right';
}> = ({ taskCustomer, taskType, defaultSla, onChange, expandInPlace, align }) => {
  align = align || 'left';
  const [filter, setFilter] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [sla, setSla] = React.useState(defaultSla);

  const [coords, setCoords] = React.useState<DOMRect | undefined>();
  const wrapperClass = 'sla-dropdown';

  const openPane = React.useCallback(
    (e: $FIXME) => {
      // Determine the top left corner of our bounding box
      let target: HTMLElement | null = e.target as HTMLDivElement;
      while (target) {
        if ([...(target.classList || [])].includes(wrapperClass)) break;
        target = target.parentElement;
      }
      if (!target) return; // Unable to determine container element?

      setOpen(o => !o);
      setCoords(target.getBoundingClientRect());
      e.stopPropagation();
    },
    [setOpen, setCoords],
  );

  // 1. get a list of matching SLAs
  const {
    loading,
    error,
    value: data,
  } = useAsync(
    async () =>
      apiFetch<{ slas: ISLAJson[]; contracts: IContractJson[] }>(
        null,
        `corp-api/billing-versions/slas/${taskCustomer}/${taskType}/${defaultSla ?? '-1'}`,
      ),
    [taskCustomer, taskType],
  );

  const contractById: Record<string, IContractJson> = React.useMemo(() => {
    if (!data) return {};
    const byContractId: Record<string, IContractJson> = {};
    for (const contract of data.contracts) {
      byContractId[contract._id] = contract;
    }
    return byContractId;
  }, [data]);

  // For hourly billing specs, we need to look at all the billingRates to determine
  // all the possible WorkAttribute keys
  // so we can make a universal ordering of them, so that the colorings of the work attributes
  // are consistent across different entries, even if they have different subsets of the work attributes.
  const allWorkAttributeKeys: string[] = React.useMemo(() => {
    if (!data) return [];

    const attributeKeys: Record<string, number> = {};
    for (const contract of data.contracts) {
      if (!contract.billingRates) continue;
      for (const billingRate of contract.billingRates) {
        for (const workAttribute of Object.keys((billingRate as IBillingRateJson).workAttributes)) {
          attributeKeys[workAttribute.toLowerCase()] = 1;
        }
      }
    }

    return Object.keys(attributeKeys).sort();
  }, [data]);

  const getColorOfWorkAttribute = React.useCallback(
    (color: string) => {
      const position = allWorkAttributeKeys.indexOf(color.toLowerCase());
      if (position < 0) return '#FFFFFF';
      return colorsByIndex[position % colorsByIndex.length];
    },
    [allWorkAttributeKeys],
  );

  const slas = React.useMemo(() => {
    if (!data) return undefined;
    return data.slas.slice();
  }, [data]);

  // https://stackoverflow.com/questions/3561493/is-there-a-regexp-escape-function-in-javascript/3561711#3561711
  function escapeRegex(str: string) {
    return str.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&');
  }

  const filteredSlas = React.useMemo(() => {
    if (!filter.length) return data?.slas;
    if (!data) return undefined;
    const filterText = filter.toLowerCase();
    const filterClauses = filterText.split(',');

    // filter algorithm: split the query up into comma-separated clauses.
    // each clause must either be a partial match to the SLA name / contract / id,
    // OR must entirely contain one of the hourly work attribute values.
    // Example query: "SO0597, writer, a" matches SLAs in contract "SO0597" with work attributes matching
    //  (workType) writer and (cluster) A.
    return data.slas.filter(sla =>
      filterClauses.every(clause => {
        if (
          sla.name.toLowerCase().includes(clause) ||
          sla._id.toLowerCase().includes(clause) ||
          sla.contract === clause
        )
          return true;
        if (sla.billingRate?.workAttributes) {
          for (const workAttribute of Object.values(sla.billingRate.workAttributes)) {
            if (workAttribute.length < 3) {
              // very short attributes should be whole-word matches
              const regexp = new RegExp(`\\b${escapeRegex(workAttribute.toLowerCase())}\\b`);
              if (regexp.test(clause)) {
                return true;
              }
            } else if (
              clause.includes(workAttribute.toString().toLowerCase()) ||
              workAttribute.toString().toLowerCase().includes(clause.trim())
            ) {
              return true;
            }
          }
        }
        return false;
      }),
    );
  }, [data, filter]);

  const selectedSla = React.useMemo(() => {
    if (!data || !sla) return undefined;
    return data.slas.find(_sla => _sla._id === sla);
  }, [data, sla]);

  const options = React.useMemo(() => {
    if (!slas || !slas.length) return <div />;
    const style = coords
      ? align === 'left'
        ? {
            top: coords.bottom + 'px',
            left: coords.left + 'px',
          }
        : {
            top: coords.bottom + 'px',
            right: window.innerWidth - coords.right + 'px',
          }
      : {};

    return (
      <div className="sla-dropdown__body" style={style}>
        <style jsx>{`
          .filter {
            padding: 5px 8px;
            border: 1px solid #eee;
            border-radius: 3px;
            width: 100%;
            box-sizing: border-box;
          }
        `}</style>
        <div className={'search'}>
          <input
            className={'filter'}
            placeholder={'Filter (example: "writer, expert tier 3, A" or "SO0597")'}
            type={'search'}
            value={filter}
            onChange={e => {
              setFilter(e.target.value?.toString());
            }}
          />
        </div>
        {filteredSlas?.map(_sla => (
          <SLAPill
            onClick={() => {
              setSla(_sla._id);
              onChange?.(_sla._id);
              setOpen(false);
            }}
            key={_sla._id}
            sla={_sla}
            contract={contractById[_sla.contract]}
            isActive={sla === _sla._id}
            getColorOfWorkAttribute={getColorOfWorkAttribute}
          />
        ))}
        {slas.length > 0 && filteredSlas?.length === 0 && (
          <div style={{ padding: '10px' }}>No SLAs matched your filters.</div>
        )}
      </div>
    );
  }, [slas, coords, filter]);

  return (
    <div>
      <style jsx global>{`
        .sla-dropdown {
          border-radius: 3px;
          position: relative;
          cursor: pointer;
        }
        .head {
          box-shadow: 5px 5px 5px #eee;
          max-width: 500px;
          border-radius: 5px;
        }
        .sla-dropdown__body {
          position: absolute;
          border: 1px solid #ccc;
          opacity: 0.95;
          box-shadow: 3px 3px 3px #eee;
          border-radius: 5px;
          cursor: default;
          color: black;
          width: auto;
          background: white;
          z-index: 9999;
          width: 100%;
          max-width: 500px;
          box-sizing: border-box;
          max-height: 300px;
          overflow-x: scroll;
        }

        .sla-dropdown--inplace .sla-dropdown__body {
          position: static;
        }
      `}</style>

      {loading && <QMLoadingPage />}
      {error && <QMErrorPage error={error} />}
      {slas && !slas.length && !filter.length && (
        <div>No existing billing SLAs found for this task customer and task type.</div>
      )}
      {slas && slas.length > 0 && (
        <div className={wrapperClass + (expandInPlace ? ' sla-dropdown--inplace' : '')}>
          <div className={'head'} onClick={openPane}>
            {selectedSla && contractById && contractById[selectedSla.contract] ? (
              <SLAPill
                sla={selectedSla}
                contract={contractById[selectedSla.contract]}
                getColorOfWorkAttribute={getColorOfWorkAttribute}
              />
            ) : (
              <div style={{ padding: '10px' }}>Choose SLA...</div>
            )}
          </div>
          {expandInPlace && open && options}
          {!expandInPlace && open && coords && (
            <Portal
              onOutsideClick={() => {
                open && setOpen(false);
              }}
            >
              {options}
            </Portal>
          )}
        </div>
      )}
    </div>
  );
};

// Used in deep project clone dynamic corp tool, can figure out what customer/task type to use
export const DynamicSlaPicker: React.FC<{
  sourceProject: string;
  targetCustomer?: string;
  targetProject?: string;
  targetProjectStrategy: TargetProjectStrategy;
  defaultSla?: string;
  onChange?: CallableFunction;
}> = ({
  sourceProject,
  targetCustomer,
  targetProject,
  targetProjectStrategy,
  defaultSla,
  onChange,
}) => {
  const [taskCustomer, setTaskCustomer] = React.useState<string | undefined>();
  const [taskType, setTaskType] = React.useState<string | undefined>();
  const queryCount = React.useRef(0);

  React.useEffect(() => {
    (async () => {
      queryCount.current++;
      const initialQueryCount = queryCount.current;

      try {
        const { taskCustomer, taskType } = await apiFetch<{
          taskCustomer: string;
          taskType: string;
        }>(null, `corp-api/billing-versions/slas/deep-clone-params`, {
          query: {
            sourceProject,
            targetCustomer,
            targetProject,
            targetProjectStrategy,
          },
        });

        if (initialQueryCount !== queryCount.current) {
          // cancel -- another lookup happened after we initiated ours
          return;
        }

        // set the taskCustomer, taskType
        setTaskCustomer(taskCustomer);
        setTaskType(taskType);
      } catch (error) {
        setTaskCustomer(undefined);
        setTaskType(undefined);
      }
    })();
  }, [
    setTaskCustomer,
    setTaskType,
    sourceProject,
    targetCustomer,
    targetProject,
    targetProjectStrategy,
    queryCount,
  ]);

  if (!taskCustomer || !taskType) {
    return <div>Please select target customer, project, etc first in order to choose an SLA.</div>;
  }

  return (
    <SLAPicker
      taskCustomer={taskCustomer}
      taskType={taskType}
      defaultSla={defaultSla}
      onChange={onChange}
      expandInPlace={true}
    />
  );
};
